import { COLORS, IconButton, SayIcon, Text } from "@asayinc/component-library";
import { Box, Collapse, Menu, MenuItem, Stack } from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { useState } from "react";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  TABULATION_TYPE_LABELS,
  TabulationReport,
} from "src/store/tabulation/types";
import { useTabulationReportInstitutionNotificationsQuery } from "src/store/tabulation";
import { ResendTabulationReportDialog } from "src/components/organisms/ResendTabulationReportDialog";
import { getTabulationInfo } from "./utils";
import { useCurrentUserQuery } from "src/store/user";

interface ITabulationItemProps {
  tabulation: TabulationReport;
}

export function TabulationItem({ tabulation }: ITabulationItemProps) {
  const { id: proxyId } = useParams() as { id: string };
  const { data: currentUser } = useCurrentUserQuery();
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLButtonElement | null>(
    null
  );
  const [isExpanded, setIsExpanded] = useState(false);
  const [resendDialogOpen, setResendDialogOpen] = useState(false);
  const { data: institutionNotifications, isLoading: isLoadingNotifications } =
    useTabulationReportInstitutionNotificationsQuery(
      { proxyId, reportId: tabulation.id },
      { skip: !isExpanded }
    );

  const isAdmin =
    currentUser?.isIntermediaryPortalAdmin ||
    currentUser?.isIntermediaryPortalSuperAdmin;

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleDownloadClick = () => {
    if (tabulation.url) {
      window.open(tabulation.url, "_blank");
    }
    handleMenuClose();
  };

  const handleResendClick = () => {
    handleMenuClose();
    setResendDialogOpen(true);
  };

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const tabulationInfo = getTabulationInfo(tabulation);

  return (
    <Stack>
      <ResendTabulationReportDialog
        isOpen={resendDialogOpen}
        onClose={() => setResendDialogOpen(false)}
        reportId={tabulation.id}
      />
      <Stack
        direction="row"
        sx={{
          py: 3,
          borderBottom: `1px solid ${COLORS.P_OUTLINE}`,
        }}
        alignItems="center"
      >
        <IconButton
          onClick={handleToggleExpand}
          sx={{ ml: 1 }}
          data-testid="expand-button"
        >
          {isExpanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
        <Stack sx={{ ml: 2 }} flexGrow={1}>
          <Text
            variant="body3"
            sx={{ mb: 1, color: COLORS.P_MED_LOW_EMPHASIS }}
          >
            Tabulation
          </Text>
          <Text variant="body2">{`${
            TABULATION_TYPE_LABELS[tabulation.type]
          } Tabulation Report`}</Text>
          <Stack direction="row" alignItems="center" sx={{ mt: 3 }}>
            <Stack sx={{ mr: 1 }} alignItems="center">
              <SayIcon
                name={tabulationInfo.iconName}
                size="16px"
                color={tabulationInfo.color}
              />
            </Stack>
            <Text variant="body3" sx={{ color: tabulationInfo.color }}>
              {tabulationInfo.label}
            </Text>
          </Stack>
        </Stack>
        <IconButton onClick={handleMenuOpen} data-testid="more-button">
          <SayIcon name="VerticalMore" size="20px" />
        </IconButton>
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleDownloadClick} disabled={!tabulation.url}>
            Download
          </MenuItem>
          {isAdmin && <MenuItem onClick={handleResendClick}>Resend</MenuItem>}
        </Menu>
      </Stack>

      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Box sx={{ pl: 8, pr: 3, py: 4, backgroundColor: "#FAFAFA" }}>
          {isLoadingNotifications ? (
            <Text variant="body3" sx={{ color: COLORS.P_MED_LOW_EMPHASIS }}>
              Loading notifications...
            </Text>
          ) : institutionNotifications &&
            institutionNotifications.length > 0 ? (
            <Stack>
              <Text variant="body3" sx={{ fontWeight: 600, mb: 3 }}>
                Notifications
              </Text>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "300px 250px 1fr",
                  gap: 2,
                  py: 2,
                  px: 2,
                  borderBottom: "1px solid #e0e0e0",
                  alignItems: "center",
                }}
              >
                <Text variant="body3" sx={{ fontWeight: 600 }}>
                  To
                </Text>
                <Text variant="body3" sx={{ fontWeight: 600 }}>
                  Sent At
                </Text>
                <Text variant="body3" sx={{ fontWeight: 600 }}>
                  Status
                </Text>
              </Box>
              {institutionNotifications.map((notification) => (
                <Box
                  key={notification.id}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "300px 250px 1fr",
                    gap: 2,
                    py: 2,
                    px: 2,
                    borderBottom: "1px solid #f5f5f5",
                    alignItems: "center",
                  }}
                >
                  <Text variant="body2">
                    {notification.toEmailAddresses.join(", ")}
                  </Text>
                  <Text variant="body2">
                    {notification.sentAt
                      ? dayjs(notification.sentAt).format("MM/DD/YYYY HH:mm")
                      : "-"}
                  </Text>
                  <Box>
                    {notification.sentAt ? (
                      <Box
                        sx={{
                          display: "inline-block",
                          px: 2,
                          py: 1,
                          borderRadius: "4px",
                          backgroundColor: "#E8F5E9",
                          color: "#2E7D32",
                        }}
                      >
                        <Text variant="body3">Sent</Text>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: "inline-block",
                          px: 2,
                          py: 1,
                          borderRadius: "4px",
                          backgroundColor: "#FFF3E0",
                          color: "#E65100",
                        }}
                      >
                        <Text variant="body3">Pending</Text>
                      </Box>
                    )}
                  </Box>
                </Box>
              ))}
            </Stack>
          ) : (
            <Text variant="body3" sx={{ color: COLORS.P_MED_LOW_EMPHASIS }}>
              No notifications available
            </Text>
          )}
        </Box>
      </Collapse>
    </Stack>
  );
}
